/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { FeatureCollection, Geometry } from 'geojson';
import type { RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { CoreAppStore } from '@opengeoweb/store';
import {
  drawingToolActions,
  genericSelectors,
  layerActions,
  uiSelectors,
  uiTypes,
} from '@opengeoweb/store';
import { LayerType } from '@opengeoweb/webmap';
import type { DrawMode } from '@opengeoweb/webmap-react';

interface UseGeoJSONHandlingProps {
  storedGeoJSON: FeatureCollection<Geometry> | undefined;
  storedEndGeoJSON?: FeatureCollection<Geometry> | undefined;
  startGeoJSON: FeatureCollection<Geometry>;
  endGeoJSON?: FeatureCollection<Geometry>;
  setGeoJSON: (geoJSON: FeatureCollection<Geometry>) => void;
  setEndGeoJSON?: (geoJSON: FeatureCollection<Geometry>) => void;
  updateStartGeoJson: (geoJSON: FeatureCollection<Geometry>) => void;
  updateEndGeoJson?: (geoJSON: FeatureCollection<Geometry>) => void;
  deactivateStartTool: () => void;
  deactivateEndTool?: () => void;
  changeActiveStartTool: (newTool: DrawMode) => void;
  changeActiveEndTool?: (newTool: DrawMode) => void;
  startEditModes: DrawMode[];
  endEditModes?: DrawMode[];
  currentStartDrawTool?: string;
  currentEndDrawTool?: string;
}

export const useGeoJSONHandling = ({
  storedGeoJSON,
  storedEndGeoJSON,
  startGeoJSON,
  endGeoJSON,
  updateStartGeoJson,
  updateEndGeoJson,
  deactivateStartTool,
  deactivateEndTool,
  changeActiveStartTool,
  changeActiveEndTool,
  startEditModes,
  endEditModes,
  currentStartDrawTool,
  currentEndDrawTool,
}: UseGeoJSONHandlingProps): {
  onChangeStartTool: (newTool: DrawMode) => void;
  onChangeEndTool: (newTool: DrawMode) => void;
  drawToolsDisabled: boolean;
} => {
  const dispatch = useDispatch();

  const sourcePanelId = useSelector((store: CoreAppStore) =>
    uiSelectors.getSourcePanelId(store, uiTypes.DialogTypes.PublicWarnings),
  );
  const warningsDialogOpen = useSelector((store: CoreAppStore) =>
    uiSelectors.getisDialogOpen(store, uiTypes.DialogTypes.PublicWarnings),
  );
  const linkedMaps: string[] = useSelector((store) =>
    genericSelectors.getLinkedMapIds(store, sourcePanelId!),
  );
  const layersById = useSelector(
    (state: CoreAppStore) => state.layers?.byId || {},
  );

  const drawToolsDisabled = warningsDialogOpen
    ? linkedMaps.length === 0
    : false;

  const reduxActiveStart = useSelector(
    (state: CoreAppStore) =>
      state.drawingtools?.entities['sigmetStartGeometry']?.activeDrawModeId as
        | string
        | undefined,
  );

  const reduxActiveEnd = useSelector(
    (state: CoreAppStore) =>
      state.drawingtools?.entities['sigmetEndGeometry']?.activeDrawModeId as
        | string
        | undefined,
  );

  const activeStartDrawTool = reduxActiveStart ?? currentStartDrawTool;
  const activeEndDrawTool = reduxActiveEnd ?? currentEndDrawTool;

  const prevActiveToolRef: RefObject<{
    start: string | undefined;
    end: string | undefined;
  }> = useRef({ start: undefined, end: undefined });
  const linkedMapsRef = useRef<string[]>(linkedMaps);

  const registeredToolsRef = useRef<{ start: Set<string>; end: Set<string> }>({
    start: new Set(),
    end: new Set(),
  });

  // remove drawn layers when component unmounts
  useEffect(() => {
    return (): void => {
      linkedMaps.forEach((mapId) => {
        dispatch(
          layerActions.layerDelete({
            mapId,
            layerId: `drawLayer-sigmetStartGeometry-${mapId}`,
          }),
        );
        dispatch(
          layerActions.layerDelete({
            mapId,
            layerId: `drawLayer-sigmetEndGeometry-${mapId}`,
          }),
        );
      });
    };
  }, [linkedMaps, dispatch]);

  const handleToolDeactivation = (
    prevTool: string | undefined,
    activeTool: string | undefined,
    storedGeoJSON: FeatureCollection<Geometry> | undefined,
    fallbackGeoJSON: FeatureCollection<Geometry>,
    deactivateTool: () => void,
    updateGeoJson: (geoJSON: FeatureCollection<Geometry>) => void,
  ): void => {
    const geoJSONToUse =
      storedGeoJSON && storedGeoJSON.features.length > 0
        ? storedGeoJSON
        : fallbackGeoJSON;

    if (prevTool && !activeTool && geoJSONToUse.features.length > 0) {
      deactivateTool();
      updateGeoJson(geoJSONToUse);
    }
  };

  useEffect(() => {
    handleToolDeactivation(
      prevActiveToolRef.current.start,
      activeStartDrawTool,
      storedGeoJSON,
      startGeoJSON,
      deactivateStartTool,
      updateStartGeoJson,
    );
    prevActiveToolRef.current.start = activeStartDrawTool;
  }, [
    activeStartDrawTool,
    storedGeoJSON,
    startGeoJSON,
    deactivateStartTool,
    updateStartGeoJson,
  ]);

  useEffect(() => {
    handleToolDeactivation(
      prevActiveToolRef.current.end,
      activeEndDrawTool,
      storedEndGeoJSON,
      endGeoJSON!,
      deactivateEndTool || ((): void => {}),
      updateEndGeoJson || ((): void => {}),
    );
    prevActiveToolRef.current.end = activeEndDrawTool;
  }, [
    activeEndDrawTool,
    storedEndGeoJSON,
    deactivateEndTool,
    updateEndGeoJson,
    endGeoJSON,
  ]);

  useEffect(() => {
    const removedMapIds = linkedMapsRef.current.filter(
      (mapId) => !linkedMaps.includes(mapId),
    );

    removedMapIds.forEach((mapId) => {
      registeredToolsRef.current.start.delete(mapId);
      registeredToolsRef.current.end.delete(mapId);
    });

    linkedMapsRef.current = linkedMaps;
  }, [linkedMaps]);

  const ensureActiveToolLayers = useCallback(
    (
      type: 'start' | 'end',
      activeTool: string | undefined,
      geoJSON: FeatureCollection<Geometry> | undefined,
      fallbackGeoJSON: FeatureCollection<Geometry> | undefined,
      editModes: DrawMode[] | undefined,
    ): void => {
      if (!activeTool || linkedMaps.length === 0) {
        return;
      }

      const drawToolId = `sigmet${type === 'start' ? 'Start' : 'End'}Geometry`;
      const toolKey = type === 'start' ? 'start' : 'end';
      const geoJSONToUse =
        geoJSON && geoJSON.features.length > 0 ? geoJSON : fallbackGeoJSON;

      linkedMaps.forEach((mapId) => {
        const geometryLayerId = `drawLayer-${drawToolId}-${mapId}`;

        if (
          !registeredToolsRef.current[toolKey].has(mapId) ||
          !layersById[geometryLayerId]
        ) {
          dispatch(
            layerActions.addLayer({
              mapId,
              layer: {
                geojson: geoJSONToUse || {
                  type: 'FeatureCollection',
                  features: [],
                },
                layerType: LayerType.featureLayer,
              },
              layerId: geometryLayerId,
              origin: 'PublicWarningsForm',
            }),
          );

          registeredToolsRef.current[toolKey].add(mapId);
        }
      });

      const activeMapId = linkedMaps[linkedMaps.length - 1];
      if (!activeMapId) {
        return;
      }

      const activeLayerId = `drawLayer-${drawToolId}-${activeMapId}`;

      dispatch(
        drawingToolActions.registerDrawTool({
          drawToolId,
          defaultDrawModes: editModes,
        }),
      );

      dispatch(
        drawingToolActions.updateGeoJSONLayerId({
          drawToolId,
          geoJSONLayerId: activeLayerId,
        }),
      );

      const activeDrawMode = editModes?.find(
        (drawMode) => drawMode.drawModeId === activeTool,
      );

      if (activeDrawMode?.isSelectable) {
        dispatch(
          layerActions.toggleFeatureMode({
            layerId: activeLayerId,
            isInEditMode: true,
            drawMode: activeDrawMode.value,
          }),
        );
      }
    },
    [dispatch, layersById, linkedMaps],
  );

  useEffect(() => {
    ensureActiveToolLayers(
      'start',
      activeStartDrawTool,
      storedGeoJSON,
      startGeoJSON,
      startEditModes,
    );
  }, [
    activeStartDrawTool,
    ensureActiveToolLayers,
    startEditModes,
    startGeoJSON,
    storedGeoJSON,
  ]);

  useEffect(() => {
    ensureActiveToolLayers(
      'end',
      activeEndDrawTool,
      storedEndGeoJSON,
      endGeoJSON,
      endEditModes,
    );
  }, [
    activeEndDrawTool,
    endEditModes,
    endGeoJSON,
    ensureActiveToolLayers,
    storedEndGeoJSON,
  ]);

  const onChangeTool = useCallback(
    (
      type: 'start' | 'end',
      newTool: DrawMode,
      deactivateOtherTool: () => void,
      changeActiveTool: (newTool: DrawMode) => void,
      editModes: DrawMode[] | undefined,
    ): void => {
      deactivateOtherTool();
      changeActiveTool(newTool);

      const toolKey = type === 'start' ? 'start' : 'end';
      const drawToolId = `sigmet${type === 'start' ? 'Start' : 'End'}Geometry`;

      let geoJSONToUse: FeatureCollection<Geometry> | undefined;

      if (type === 'start') {
        if (!storedGeoJSON && startGeoJSON.features.length > 0) {
          geoJSONToUse = startGeoJSON;
        } else {
          geoJSONToUse = storedGeoJSON;
        }
      } else if (
        !storedEndGeoJSON &&
        endGeoJSON &&
        endGeoJSON?.features.length! > 0
      ) {
        geoJSONToUse = endGeoJSON;
      } else {
        geoJSONToUse = storedEndGeoJSON;
      }

      linkedMaps.forEach((mapId) => {
        const geometryLayerId = `drawLayer-${drawToolId}-${mapId}`;

        if (!registeredToolsRef.current[toolKey].has(mapId)) {
          dispatch(
            layerActions.addLayer({
              mapId,
              layer: {
                geojson: geoJSONToUse || {
                  type: 'FeatureCollection',
                  features: [],
                },
                layerType: LayerType.featureLayer,
              },
              layerId: geometryLayerId,
              origin: 'PublicWarningsForm',
            }),
          );

          dispatch(
            drawingToolActions.registerDrawTool({
              drawToolId,
              defaultDrawModes: editModes,
            }),
          );

          dispatch(
            drawingToolActions.updateGeoJSONLayerId({
              drawToolId,
              geoJSONLayerId: geometryLayerId,
            }),
          );

          registeredToolsRef.current[toolKey].add(mapId);
        }
      });

      dispatch(
        drawingToolActions.changeDrawToolMode({
          drawToolId,
          drawModeId: newTool.drawModeId,
        }),
      );

      if (!newTool.isSelectable) {
        if (type === 'start') {
          deactivateStartTool();
          updateStartGeoJson(newTool.shape as GeoJSON.FeatureCollection);
        } else {
          deactivateEndTool?.();
          updateEndGeoJson?.(newTool.shape as GeoJSON.FeatureCollection);
        }
      }
    },
    [
      storedEndGeoJSON,
      endGeoJSON,
      linkedMaps,
      dispatch,
      storedGeoJSON,
      startGeoJSON,
      updateStartGeoJson,
      updateEndGeoJson,
      deactivateStartTool,
      deactivateEndTool,
    ],
  );

  return {
    onChangeStartTool: (newTool: DrawMode) =>
      onChangeTool(
        'start',
        newTool,
        deactivateEndTool || ((): void => {}),
        changeActiveStartTool,
        startEditModes,
      ),
    onChangeEndTool: (newTool: DrawMode) =>
      onChangeTool(
        'end',
        newTool,
        deactivateStartTool,
        changeActiveEndTool || ((): void => {}),
        endEditModes,
      ),
    drawToolsDisabled,
  };
};
